import { RTLLanguages } from 'imports/generator/api/constants';
import { isJobTrackEnable, removeExperiment, websiteFontLoad } from 'lib/helpers';
import moment from 'moment';
import nookies from 'nookies';
import PropTypes from 'prop-types';
import { Fragment, memo, useEffect, useMemo } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { v4 as uuidV4 } from 'uuid';
import Meta from '/components/Meta';
import { BLUEPILL_HOSTS } from '/imports/core/api/constants';
import useIntl from '/imports/core/api/useIntl';
import { ToasterProvider } from '/imports/core/api/useToaster';
import useTracking from '/imports/core/hooks/useTracking';
import Toaster from '/imports/core/ui/components/Toaster';
import { userActivity } from '/imports/job-tracking/api/api';

import GlobalStyle from '/styles/general';
import Theme from '/styles/theme';

const StyledPage = styled.section`
  display: ${({ $localIsLoading }) => ($localIsLoading ? 'none' : 'unset')};
  color: black;
`;

const PageRender = ({ host, route, children }) => {
  const setJobTrackTheme = route === '/login';
  const { locale, t, isLoading } = useIntl();
  const tracking = useTracking();
  const { trackEvent } = tracking;
  const { token } = nookies.get({});
  const isAutoApply = BLUEPILL_HOSTS.includes(host);
  const isAutoApplyRoute = ['/', '/resume-options', '/apply-form', '/checkout'];

  const isHtmlPreview = useMemo(() => route === '/resume-html', [route]);

  const theme = useMemo(
    () => ({
      ...Theme,
      font: { ...Theme.font, family: { ...Theme.font.family, ...websiteFontLoad(locale) } },
      isRTL: RTLLanguages.includes(locale),
      ...(setJobTrackTheme && { isJobTrack: isJobTrackEnable() }),
    }),
    [locale, setJobTrackTheme],
  );

  useEffect(() => {
    const removeExp = async () => removeExperiment('experiment_resume_nation_global');
    removeExp();
  }, []);

  useEffect(() => {
    if (isAutoApply && isAutoApplyRoute.includes(route)) {
      document.body.style.backgroundColor = '#070d2d';
    }
  }, []);

  useEffect(() => {
    const userAction = async () => {
      if (token && isJobTrackEnable()) {
        userActivity(token);
      }
    };
    userAction();
  }, [token]);

  useEffect(() => {
    if (!token) localStorage.setItem('guestId', uuidV4());
  }, [token]);

  useEffect(() => {
    const lastActionDate = localStorage.getItem('DATE_LAST_ACTION');
    const email = localStorage.getItem('email');

    if (!email) return;

    const todayFormatted = moment().format('DD-MM-YYYY');

    if (!lastActionDate) {
      localStorage.setItem('DATE_LAST_ACTION', todayFormatted);
      if (typeof window !== 'undefined' && window.sendinblue?.identify && email) {
        window.sendinblue.identify(email, { DATE_LAST_ACTION: todayFormatted });
      }
      trackEvent('DATE_LAST_ACTION', todayFormatted);
    } else if (moment().diff(lastActionDate, 'days') >= 1) {
      if (typeof window !== 'undefined' && window.sendinblue?.identify && email) {
        window.sendinblue.identify(email, { DATE_LAST_ACTION: lastActionDate });
      }
      trackEvent('DATE_LAST_ACTION', todayFormatted);
    } else {
      localStorage.setItem('DATE_LAST_ACTION', todayFormatted);
    }
  }, [trackEvent]);

  return (
    <ThemeProvider theme={theme}>
      <ToasterProvider>
        <Fragment>
          {!isHtmlPreview && <GlobalStyle />}
          {!isHtmlPreview && <Meta domain={host} />}
          <StyledPage $localIsLoading={isLoading && !isHtmlPreview}>{children}</StyledPage>
          <Toaster />
        </Fragment>
      </ToasterProvider>
    </ThemeProvider>
  );
};

PageRender.propTypes = {
  host: PropTypes.string,
  route: PropTypes.string,
  children: PropTypes.node,
};

export default memo(PageRender);
